import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown, Button, Spin, notification, Typography, Space, theme } from 'antd';
import { ApartmentOutlined, CheckOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { listBusinessOrganisations } from '../../actions/businessOrganisationActions';
import { mergeOrganizationSwitchUserInfo } from '../../utils/authUserInfoUtils';

const { Text } = Typography;

const OrganizationSwitcher = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { token } = theme.useToken();
  const [switching, setSwitching] = useState(false);
  
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  
  const businessOrganisationList = useSelector((state) => state.businessOrganisationList);
  const { businessOrganisations } = businessOrganisationList;

  const [api, contextHolder] = notification.useNotification();

  const showNotification = (type, message, description) => {
    api[type]({
      message,
      description,
      placement: 'bottom',
    });
  };

  const handleOrganizationSwitch = async (organizationId) => {
    if (!userInfo || !userInfo.token) {
      showNotification('error', 'Error', 'User not authenticated');
      return;
    }

    if (organizationId === userInfo.businessOrganisationId) {
      return; // Already selected
    }

    setSwitching(true);
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userInfo.token}`
        }
      };

      const { data } = await axios.post(
        '/dotnet_api/v1/auth/switch-organization',
        { businessOrganisationId: organizationId },
        config
      );

      const updatedUserInfo = mergeOrganizationSwitchUserInfo(userInfo, data);

      // Update localStorage
      localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));

      // Update Redux state
      dispatch({
        type: 'USER_LOGIN_SUCCESS',
        payload: updatedUserInfo
      });

      showNotification('success', 'Success', `Switched to ${data.businessOrganisationName}`);
      
      // Reload the page to refresh the application state
      window.location.reload();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to switch organization';
      showNotification('error', 'Error', errorMessage);
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    if (userInfo?.availableOrganisations?.length > 0) {
      dispatch(listBusinessOrganisations());
    }
  }, [dispatch, userInfo]);

  if (!userInfo) {
    return null;
  }

  const getUserDisplayName = () => {
    const profile = userInfo.applicationUserProfile;
    if (profile?.firstName || profile?.lastName) {
      const title = profile.title ? `${profile.title} ` : '';
      return `${title}${profile.firstName || ''} ${profile.lastName || ''}`.trim();
    }
    return userInfo.userName || userInfo.username || userInfo.email || t('User');
  };

  const getUserRoleDisplay = () => {
    if (Array.isArray(userInfo.roles) && userInfo.roles.length > 0) {
      return userInfo.roles.join(', ');
    }
    return userInfo.applicationUserProfile?.role || '';
  };

  const hasMultipleOrganisations = userInfo.availableOrganisations && userInfo.availableOrganisations.length > 1;

  const getOrganizationName = (orgId) => {
    if (orgId === userInfo.businessOrganisationId) {
      return userInfo.businessOrganisationName;
    }
    
    const org = businessOrganisations?.find(org => org.id === orgId);
    return org ? org.name : `Organization ${orgId}`;
  };

  // Dropdown menu items (only if multiple organizations)
  const menuItems = hasMultipleOrganisations ? userInfo.availableOrganisations.map((orgId) => ({
    key: orgId,
    label: (
      <div style={{ padding: '4px 0' }}>
        <Space>
          {orgId === userInfo.businessOrganisationId && (
            <CheckOutlined style={{ color: token.colorSuccess }} />
          )}
          <Text style={{ fontSize: '13px' }}>
            {getOrganizationName(orgId)}
          </Text>
        </Space>
      </div>
    ),
    onClick: () => handleOrganizationSwitch(orgId)
  })) : [];

  const userRoleDisplay = getUserRoleDisplay();

  return (
    <>
      {contextHolder}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            height: '32px',
          }}
        >
          <UserOutlined style={{ fontSize: '16px', color: token.colorPrimary }} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
            {userRoleDisplay && (
              <Text
                style={{
                  fontSize: '12px',
                  color: token.colorTextSecondary,
                  lineHeight: 1,
                }}
              >
                {userRoleDisplay}
              </Text>
            )}
            <Text
              style={{
                fontSize: '13px',
                color: token.colorText,
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {getUserDisplayName()}
            </Text>
          </div>
        </div>

        {userInfo.businessOrganisationName && (
          <Dropdown
            menu={{ items: menuItems }}
            placement="bottomRight"
            trigger={hasMultipleOrganisations ? ['click'] : []}
            disabled={switching || !hasMultipleOrganisations}
          >
            <Button
              type="text"
              loading={switching}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                height: '32px',
                borderRadius: '4px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <ApartmentOutlined style={{ fontSize: '16px', color: token.colorPrimary }} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                <Text
                  style={{
                    fontSize: '12px',
                    color: token.colorTextSecondary,
                    lineHeight: 1,
                  }}
                >
                  {t('Current Organisation')}
                </Text>
                <Text
                  style={{
                    fontSize: '13px',
                    color: token.colorText,
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  {userInfo.businessOrganisationName || t('Organization')}
                </Text>
              </div>
            </Button>
          </Dropdown>
        )}
      </div>
    </>
  );
};

export default OrganizationSwitcher; 